import {
  FormControl,
  FormControlLabel,
  FormLabel,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  PercentageIcon,
  RadioGroup,
  RupeeIcon,
  Show,
  Typography,
} from "components";
import { FeePlansForm } from "interfaces/Revenue";
import { Controller, useFormContext } from "react-hook-form";
import { validationForPercentage } from "utils/helpers";
import {
  ADVANCE_TYPE,
  BILLING_FREQUENCY,
  FEE_PARAMETER,
  FEE_TYPE,
  MANAGEMENT_CALCULATION_METHOD,
} from "../utils";

const ManagementFeeForm = () => {
  const form = useFormContext<FeePlansForm>();

  const watchFeeType = form.watch("managementFee.feeType");
  const watchCalculationMethod = form.watch("managementFee.calculationMethod");

  return (
    <Grid2 container mt={2} spacing={3}>
      <Grid2
        size={12}
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Typography fontSize={14} fontWeight={700} color=" #20272A">
          Management Fee
        </Typography>
      </Grid2>

      <Grid2 size={6} container spacing={3}>
        <Grid2 size={8}>
          <Controller
            name="managementFee.name"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field.value ?? ""}
                size="medium"
                placeholder="Product Name"
                tooltipTitle="This name will appear on the invoices generated for your clients."
                inputLabel={"Product Name"}
              />
            )}
          />
        </Grid2>
        <Grid2 size={4}>
          <Controller
            name="managementFee.hsnSacCode"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please enter HSN code",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                value={field.value ?? ""}
                size="medium"
                requiredField
                placeholder="HSN Code"
                inputLabel={"HSN Code"}
                tooltipTitle="This code will appear on invoices to classify your product for tax compliance."
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="managementFee.feeType"
            control={form.control}
            rules={{
              required: { value: true, message: "Please select a Fee Type" },
            }}
            render={({ field }) => (
              <FormControl>
                <FormLabel
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(----black)",
                  }}
                >
                  Fee Type
                </FormLabel>
                <RadioGroup
                  row
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value);

                    if (e.target.value === "fixed") {
                      form.setValue("managementFee.calculationMethod", "");
                    }
                  }}
                >
                  {FEE_TYPE.map((item) => {
                    return (
                      <FormControlLabel
                        key={item.name}
                        value={item.value}
                        control={<GKRadio disableLeftPadding />}
                        label={item.name}
                      />
                    );
                  })}
                </RadioGroup>
              </FormControl>
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="managementFee.calculationMethod"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select a Calculation Method",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="Calculation Method"
                disableClearable={false}
                options={MANAGEMENT_CALCULATION_METHOD.filter((item) =>
                  watchFeeType === "Flat Fee"
                    ? item.value !== "Advance Trailing"
                    : item,
                )}
                onChange={(e, val) => field.onChange(val?.value)}
                value={
                  MANAGEMENT_CALCULATION_METHOD.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || ""
                }
                getOptionLabel={(option) => option.name || option}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Show.When isTrue={watchCalculationMethod === "Advance"}>
          <Grid2 size={6}>
            <Controller
              name="managementFee.advanceType"
              control={form.control}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Advance Type"
                  disableClearable={false}
                  options={ADVANCE_TYPE}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    ADVANCE_TYPE.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Show.When>
        <Show.When isTrue={watchFeeType === "AUM Based"}>
          <Grid2 size={6}>
            <Controller
              name="managementFee.feeParameters"
              control={form.control}
              rules={{
                required: {
                  value: true,
                  message: "Please select a Fee Parameter",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  inputLabel="Fee Parameter"
                  disableClearable={false}
                  options={FEE_PARAMETER.filter((item) =>
                    watchCalculationMethod !== "Advance"
                      ? ![
                          "Opening AUM Current Value",
                          "Opening AUM Portfolio Value",
                          "Cash Flow Pro Rata",
                        ].includes(item.value)
                      : watchCalculationMethod === "Advance"
                        ? [
                            "Opening AUM Current Value",
                            "Opening AUM Portfolio Value",
                            "Cash Flow Pro Rata",
                          ].includes(item.value)
                        : item,
                  )}
                  onChange={(e, val) => field.onChange(val?.value)}
                  value={
                    FEE_PARAMETER.find(
                      (dataItem) => dataItem.value === field.value,
                    ) || ""
                  }
                  getOptionLabel={(option) => option.name || option}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid2>
        </Show.When>

        <Grid2 size={6}>
          <Controller
            name="managementFee.fee"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please enter Fee Amount",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKTextField
                {...field}
                value={field.value ?? ""}
                requiredField
                inputLabel={
                  watchFeeType === "Flat Fee" ? "Fee Amount" : "Fee Percentage"
                }
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  field.onChange(
                    watchFeeType === "Flat Fee"
                      ? e.target.valueAsNumber
                      : validationForPercentage(e.target.valueAsNumber),
                  );
                }}
                slotProps={{
                  input: {
                    endAdornment:
                      watchFeeType === "Flat Fee" ? (
                        <RupeeIcon />
                      ) : (
                        <PercentageIcon />
                      ),
                  },
                }}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="managementFee.billingFrequency"
            control={form.control}
            rules={{
              required: {
                value: true,
                message: "Please select Billing Frequency",
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                inputLabel="Billing Frequency"
                disableClearable={false}
                options={BILLING_FREQUENCY || []}
                onChange={(e, val) => field.onChange(val?.value)}
                value={
                  BILLING_FREQUENCY.find(
                    (dataItem) => dataItem.value === field.value,
                  ) || ""
                }
                getOptionLabel={(option) => option.name || option}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid2>
        <Grid2 size={6}>
          <Controller
            name="managementFee.outOfPocketExpense"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field.value ?? ""}
                inputLabel="Out of Pocket Expenses"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.valueAsNumber)
                }
              />
            )}
          />
        </Grid2>
        <Show.When isTrue={watchFeeType === "AUM Based"}>
          <Grid2 size={6}>
            <Controller
              name="managementFee.minimumFee"
              control={form.control}
              render={({ field }) => (
                <GKTextField
                  {...field}
                  value={field.value ?? ""}
                  inputLabel="Minimum Fee"
                  type="number"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    field.onChange(e.target.valueAsNumber)
                  }
                  slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
                />
              )}
            />
          </Grid2>
        </Show.When>
        <Grid2 size={6}>
          <Controller
            name="managementFee.maximumFee"
            control={form.control}
            render={({ field }) => (
              <GKTextField
                {...field}
                value={field.value ?? ""}
                inputLabel="Maximum Fee"
                type="number"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  field.onChange(e.target.valueAsNumber)
                }
                slotProps={{ input: { endAdornment: <RupeeIcon /> } }}
              />
            )}
          />
        </Grid2>
      </Grid2>
    </Grid2>
  );
};

export default ManagementFeeForm;
